import { Config } from "@/components/crud/crud-config";
import CrudFormElements from "@/components/crud/crud-form-elements";
import CrudPage from "@/components/crud/page";
import PencilIcon from "@/components/icons/pencil";
import TableActions from "@/components/table-actions";
import PermissionsLists from "@/config/PermissionsLists";
import { EndPoints, httpService, Operation } from "@/Core/Client";
import { DATE_FORMAT_OPTIONS } from "@/Core/Helpers/DayjsHelpers";
import {
    HotelFormDto,
    hotelFormSchema,
    HotelResponseDto,
} from "@/Dtos/hotel-dto";
import DateCell from "@/ui/date-cell";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { Checkbox } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";
import EyeIcon from "@/components/icons/eye";

const config: Config<HotelResponseDto, HotelFormDto> = {
    endPoint: EndPoints.hotels.crud,
    queryKey: ["hotels"],
    columns: [],
    validationSchema: hotelFormSchema,
    fields: [
        {
            name: "name",
            label: "Name",
            type: "text",
            hasFilter: true,
            className: "col-span-full",
            filterClassName: "w-auto",
            filter: {
                operation: Operation.like,
                // showOperations: true,
            },
        },
        {
            name: "entityId",
            label: "Company",
            type: "referTo",
            hasFilter: true,
            className: "col-span-full",
            filterClassName: "w-auto",
            referEndPoint: EndPoints.entity.referTo,
            filter: {
                operation: Operation.eq,
                // showOperations: true,
            },
        },
        {
            name: "remarks",
            label: "Remarks",
            type: "text",
            hasFilter: false,
            className: "col-span-full",
            filterClassName: "w-auto",
        },
        {
            name: "createdAt",
            label: "Created At",
            type: "date",
            hasFilter: true,
            filterOnly: true,
            dateFormat: DATE_FORMAT_OPTIONS.L,
            className: "col-span-full",
            filterClassName: "w-auto",
            filter: {
                operation: Operation.gte,
                // showOperations: true,
            },
        },
    ],
    titles: {
        pageTitle: "Hotels",
        pageCreateTitle: "Add Hotel",
        pageEditTitle: "Edit Hotel",
    },
    filterProps: {
        useDrawer: false,
        // ignoreSorting: true,
        sortingClassName: "w-auto",
        // sortingMinWidth: "10px",
        directionClassName: "w-auto",
        // directionMinWidth: "10px",
    },

    permissionsCreate: PermissionsLists.Hotels.Create,
    formOptions: {
        customSize: "750px",
    },
};

const columnHelper = createColumnHelper<HotelResponseDto>();

export const columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),
    columnHelper.display({
        id: "id",
        size: 100,
        header: "#",
        cell: ({ row }) => <>{row.index + 1}</>,
    }),
    columnHelper.accessor("name", {
        id: "name",
        size: 200,
        header: "Name",
        cell: ({ row }) => row.original.name,
    }),
    columnHelper.accessor("entity.name", {
        id: "entity.name",
        size: 200,
        header: "Company",
        cell: ({ row }) => row.original.entity?.name,
    }),
    columnHelper.accessor("remarks", {
        id: "remarks",
        size: 200,
        header: "Remarks",
        cell: ({ row }) => row.original.remarks,
    }),
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        size: 200,
        header: "Created",
        cell: ({ row }) => (
            <DateCell
                date={new Date(Date.parse(row.original?.createdAt ?? ""))}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const { openModal } = useModal();
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();

            return (
                <TableActions
                    row={row.original}
                    onDelete={() => {
                        setLoading(true);
                        httpService(config.endPoint).delete(row.original.id!);
                        queryClient.refetchQueries({
                            queryKey: config.queryKey,
                        });
                        setLoading(false);
                    }}
                    actions={[
                        {
                            title: "Details",
                            color: "gray",
                            isLoading: isLoading,
                            icon: <EyeIcon className="size-4" />,
                            onClick: () =>
                                openModal({
                                    view: <CrudFormElements<HotelFormDto, HotelResponseDto>
                                        editData={row.original}
                                        config={config as any}
                                    />,
                                    customSize: config.formOptions?.customSize,
                                }),
                        },
                        {
                            title: "Edit",
                            color: "gray",
                            isLoading: isLoading,
                            icon: <PencilIcon className="size-4" />,
                            onClick: () =>
                                openModal({
                                    view: <CrudFormElements<HotelFormDto, HotelResponseDto>
                                        editData={row.original}
                                        config={config as any}
                                    />,
                                    customSize: config.formOptions?.customSize,
                                }),
                        },
                    ]}
                />
            );
        },
    }),
];

config.columns = columns;

export default function MainPage() {
    return CrudPage<HotelResponseDto, HotelFormDto>({ config: config });
}